import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

/**
 * FrontendConfigContext – runtime configuration fetched from the backend DB.
 *
 * On mount the provider calls GET /frontend-config (public, no auth) and
 * caches the result in-memory.  Components read values via the useConfig()
 * hook which returns a plain object of key→value pairs.
 *
 * Default (fallback) values are kept here only so the UI can render before
 * the network call returns; the real source of truth is the Configuration
 * collection in MongoDB, editable from the Admin → Configuration panel.
 */

const DEFAULTS = {
  STORIES_INITIAL_PAGE_SIZE: 200,
  STORIES_LOAD_MORE_PAGE_SIZE: 200,
  ARTICLE_PAGE_SIZE: 100,
  STORY_ARTICLE_MINIMUM_BACKFILL_COUNT: 20,
  STORIES_IDLE_REFRESH_MS: 300000,
  PUBLISHER_BUCKETS_LIVE_SYNC_INTERVAL_MS: 15000,
  // Fails CLOSED. This context falls back to DEFAULTS whenever
  // /frontend-config cannot be reached, so a default of true would open
  // the public campaign pages during exactly the outage in which nothing
  // behind them works.
  CAMPAIGN_EXPERIENCE_ENABLED: false,
  /**
   * Whether the fetch has finished — false means "we do not know yet", not
   * "the value is the default".
   *
   * Every existing consumer reads a page size or a toggle and is happy to use
   * the fallback until the real value lands. A kill switch is not like that:
   * rendering its OFF state during the fetch shows a visitor "this page is not
   * available" for a moment before the page they asked for appears, which is a
   * flash of false information on a public landing page. Consumers that care
   * about the difference wait on this; nobody else has to notice it.
   */
  __loaded: false,
};

const FrontendConfigContext = createContext(DEFAULTS);

/**
 * Hook to read frontend configuration values.
 * @returns {Object} key→value map (e.g. { STORIES_INITIAL_PAGE_SIZE: 200, … })
 */
export const useConfig = () => useContext(FrontendConfigContext);

export const FrontendConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(DEFAULTS);

  useEffect(() => {
    let cancelled = false;

    const fetchConfig = async () => {
      try {
        const baseURL = process.env.REACT_APP_BASE_URL;
        const { data } = await axios.get(`${baseURL}/frontend-config`);
        if (!cancelled && data) {
          setConfig(prev => ({ ...prev, ...data, __loaded: true }));
        }
      } catch (err) {
        // Silently fall back to defaults – the UI is still usable.
        console.warn('[FrontendConfig] Could not fetch config, using defaults:', err.message);
        // Still `__loaded`: we asked and we have our answer, which is the
        // defaults. A consumer waiting on this must not wait forever, and the
        // defaults are chosen to fail closed for anything that matters.
        if (!cancelled) setConfig(prev => ({ ...prev, __loaded: true }));
      }
    };

    fetchConfig();
    return () => { cancelled = true; };
  }, []);

  return (
    <FrontendConfigContext.Provider value={config}>
      {children}
    </FrontendConfigContext.Provider>
  );
};
